import { useMemo } from "react";
import {
  Box,
  Tooltip,
  TooltipProps,
  Typography,
  Divider,
  Icon,
  Stack,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { VisitOrResourceWarningLabel } from "@caredna-marlin/utils/warnings";
import { reverse, sortBy } from "lodash";

export type PerResourceWarnings = {
  providerName: string;
  warnings: VisitOrResourceWarningLabel[];
};

export type VisitIssuesIconProps = {
  visitWarnings: VisitOrResourceWarningLabel[];
  perResource: PerResourceWarnings[];
  width?: number;
  ariaLabel?: string;
  placement?: TooltipProps["placement"];
};

const SevIcon = ({ sev, inline = false }: { sev: number; inline?: boolean }) => {
  const sx = inline ? { mr: 0.75 } : undefined;
  if (sev >= 3) return <EventBusyIcon fontSize="small" color="error" sx={sx} />;
  if (sev === 2) return <WarningAmberRoundedIcon fontSize="small" color="warning" sx={sx} />;
  if (sev === 1) return <InfoOutlinedIcon fontSize="small" color="info" sx={sx} />;
  return null;
};

const Title = ({
  visitWarnings,
  perResource,
}: {
  visitWarnings: VisitOrResourceWarningLabel[];
  perResource: PerResourceWarnings[];
}) => {
  const hasVisitWarnings = visitWarnings.length > 0;
  const hasVisitResourceWarnings = perResource.flatMap(pr => pr.warnings).length > 0

  return (
    <Box sx={{ p: 1, bgcolor: "white" }}>
      {/* Visit-level section */}
      {hasVisitWarnings && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Visit issues
          </Typography>
          {reverse(sortBy(visitWarnings, "severity")).map((w, i) => (
            <Box key={`visit-${i}`} display="flex" alignItems="center" sx={{ lineHeight: 1.4, mb: 0.25 }}>
              <SevIcon sev={w.severity} inline />
              <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
                {w.warning}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Divider only if there are visit-level warnings */}
      {hasVisitWarnings && hasVisitResourceWarnings && <Divider sx={{ my: 1 }} />}

      {/* Resource-level section */}
      <Box>
        {perResource.map((pr, i) => (
          <Box key={`res-${i}`} sx={{ mb: 1.25 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
              {pr.providerName || "Provider"}
            </Typography>
            {reverse(sortBy(pr.warnings || [], "severity")).map((w, j) => (
              <Box key={`res-${i}-${j}`} display="flex" alignItems="center" sx={{ lineHeight: 1.4, mb: 0.25 }}>
                <SevIcon sev={w.severity} inline />
                <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
                  {w.warning}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default function VisitIssuesIcon({
  visitWarnings,
  perResource,
  width = 600,
  ariaLabel = "Visit issues",
  placement = "top",
}: VisitIssuesIconProps) {
  const allWarnings = useMemo<VisitOrResourceWarningLabel[]>(() => {
    return [
      ...visitWarnings,
      ...perResource.flatMap((pr) => pr.warnings),
    ].sort((a, b) => b.severity - a.severity);
  }, [visitWarnings, perResource]);

  // Compute highest severity category plus number remaining
  const category = useMemo<string>(() => {
    if (allWarnings.length == 0) return "";

    const categories = [...new Set(allWarnings.map((w) => w.category))];
    const extras = categories.length - 1;

    return extras ? `${categories[0]} +${extras}` : categories[0];
  }, [allWarnings]);

  const resourcesWithErrors = perResource.filter((pr) => pr.warnings.length > 0)

  return (
    <Tooltip
      title={<Title visitWarnings={visitWarnings} perResource={resourcesWithErrors} />}
      placement={placement}
      arrow
      enterDelay={120}
      leaveDelay={100}
      enterNextDelay={60}
      disableFocusListener
      disableTouchListener
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "white",
            color: "text.primary",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 3,
            p: 0,
            borderRadius: 1,
            maxWidth: width,
            overflow: "visible",
          },
        },
        arrow: { sx: { color: "white" } },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Icon aria-label={ariaLabel}>
          <SevIcon sev={allWarnings.length ? allWarnings[0].severity : 0} inline/>
        </Icon>
         {category}
      </Stack>

    </Tooltip>
  );
}
